'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useState, useCallback } from 'react';
import SearchBar from '@/components/component_unique/searchbar';
import SearchFilter, { FilterData } from '@/components/component_unique/SearchFilter';
import HorizontalBigImageCategoryNewsSearch from '@/components/component_horizontal/HorizontalBigImageCategoryNewsSearch';
import { MEDIA_BASE_URL, FALLBACK_IMAGE } from '@/lib/constants';
interface FilteredStory {
  id: string;
  headline: string;
  slug: string;
  subheadline?: string;
  'hero-image-s3-key'?: string | null;
  'published-at': number;
  sections?: Array<{
    'display-name'?: string;
  }>;
  'author-name'?: string;
  'story-template'?: string;
  alternative?: {
    home?: {
      default?: {
        'hero-image'?: {
          'hero-image-s3-key'?: string;
        };
      };
    };
  };
}
interface SearchPageClientProps {
  query: string;
  totalResults?: number;
  children: React.ReactNode;
  rightSideContent: React.ReactNode;
  rightSideTopAd?: React.ReactNode;
  filterData?: FilterData;
}
interface FetchResult<T> {
  success: boolean;
  data?: T;
  error?: string;
}
interface FilterApiResponse {
  items?: FilteredStory[];
  total?: number;
  error?: string;
}

// Safe fetch helper that handles HTML responses gracefully
async function safeFetchJson<T>(url: string): Promise<FetchResult<T>> {
  try {
    const response = await fetch(url);
    const contentType = response.headers.get('content-type') || '';

    // Check if response is JSON
    if (!contentType.includes('application/json')) {
      const text = await response.text();
      const isHtml = text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html');
      if (isHtml) {
        console.error('[Filter API] Received HTML instead of JSON. API route may not be configured correctly.');
        console.error('[Filter API] URL:', url);
        console.error('[Filter API] Content-Type:', contentType);
        return {
          success: false,
          error: 'API returned HTML instead of JSON. Please restart the dev server.'
        };
      }

      // Try to parse as JSON anyway (some APIs don't set content-type correctly)
      try {
        const data = JSON.parse(text) as T;
        return {
          success: true,
          data
        };
      } catch {
        console.error('[Filter API] Response is not valid JSON:', text.substring(0, 200));
        return {
          success: false,
          error: 'Invalid response format from API'
        };
      }
    }

    // Check HTTP status
    if (!response.ok) {
      const errorText = await response.text();
      console.error('[Filter API] HTTP Error:', response.status, response.statusText);
      return {
        success: false,
        error: `API error: ${response.status} ${response.statusText}`
      };
    }

    // Parse JSON response
    const data = (await response.json()) as T;
    return {
      success: true,
      data
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error('[Filter API] Fetch error:', errorMessage);
    return {
      success: false,
      error: `Network error: ${errorMessage}`
    };
  }
}

// Helper function to format time ago
function formatTimeAgo(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (minutes < 1) return 'இப்போது';
  if (minutes < 60) {
    return minutes === 1 ? '1 நிமிடம் முன்பு' : `${minutes} நிமிடங்கள் முன்பு`;
  }
  if (hours < 24) {
    return hours === 1 ? '1 மணி நேரம் முன்பு' : `${hours} மணி நேரங்கள் முன்பு`;
  }
  // 24 hours or more - show exact date and time in IST
  const date = new Date(timestamp);
  const istOffset = 5.5 * 60 * 60 * 1000;
  const istDate = new Date(date.getTime() + istOffset + date.getTimezoneOffset() * 60 * 1000);
  const day = istDate.getDate();
  const tamilMonths = ['ஜனவரி', 'பிப்ரவரி', 'மார்ச்', 'ஏப்ரல்', 'மே', 'ஜூன்', 'ஜூலை', 'ஆகஸ்ட்', 'செப்டம்பர்', 'அக்டோபர்', 'நவம்பர்', 'டிசம்பர்'];
  const month = tamilMonths[istDate.getMonth()];
  const year = istDate.getFullYear();
  let hrs = istDate.getHours();
  const mins = istDate.getMinutes().toString().padStart(2, '0');
  const ampm = hrs >= 12 ? 'pm' : 'am';
  hrs = hrs % 12 || 12;
  return `${day} ${month} ${year}, ${hrs}:${mins} ${ampm} IST`;
}

// Build URL-safe query params
function buildFilterParams(query: string, filters: Record<string, string[] | number[] | {
  from: string;
  to: string;
}>): URLSearchParams {
  const params = new URLSearchParams();

  // Encode query safely
  params.set('q', encodeURIComponent(query));
  params.set('sort', 'latest-published');
  params.set('limit', '20');
  params.set('offset', '0');

  // Section names
  if (filters['Section-Name'] && Array.isArray(filters['Section-Name']) && filters['Section-Name'].length > 0) {
    (filters['Section-Name'] as string[]).forEach(name => {
      params.append('section-name', encodeURIComponent(name));
    });
  }

  // Authors
  if (filters.Author && Array.isArray(filters.Author) && filters.Author.length > 0) {
    (filters.Author as string[]).forEach(author => {
      params.append('author', encodeURIComponent(author));
    });
  }

  // Story types (using story-template field from API)
  if (filters['Story Type'] && Array.isArray(filters['Story Type']) && filters['Story Type'].length > 0) {
    (filters['Story Type'] as string[]).forEach(type => {
      params.append('story-template', encodeURIComponent(type));
    });
  }

  // Date filters
  if (filters.Date && typeof filters.Date === 'object' && !Array.isArray(filters.Date)) {
    const dateFilter = filters.Date as {
      from: string;
      to: string;
    };
    if (dateFilter.from) {
      params.append('published-after', new Date(dateFilter.from).getTime().toString());
    }
    if (dateFilter.to) {
      const toDate = new Date(dateFilter.to);
      toDate.setHours(23, 59, 59, 999);
      params.append('published-before', toDate.getTime().toString());
    }
  }
  return params;
}
export default function SearchPageClient(t0) {
  const $ = _c(33);
  const {
    query,
    totalResults,
    children,
    rightSideContent,
    rightSideTopAd,
    filterData
  } = t0;
  const [showFilter, setShowFilter] = useState(false);
  const [filteredStories, setFilteredStories] = useState(null);
  const [filteredTotal, setFilteredTotal] = useState(undefined);
  const [filterError, setFilterError] = useState(null);
  let t1;
  if ($[0] !== showFilter) {
    t1 = () => {
      setShowFilter(!showFilter);
    };
    $[0] = showFilter;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleFilterClick = t1;
  let t2;
  if ($[2] !== query) {
    t2 = async filters => {
      setFilterError(null);
      const hasActiveFilters = filters["Section-Name"] && Array.isArray(filters["Section-Name"]) && filters["Section-Name"].length > 0 || filters.Author && Array.isArray(filters.Author) && filters.Author.length > 0 || filters["Story Type"] && Array.isArray(filters["Story Type"]) && filters["Story Type"].length > 0 || filters.Date && typeof filters.Date === "object" && !Array.isArray(filters.Date) && ((filters.Date as {
        from: string;
        to: string;
      }).from || (filters.Date as {
        from: string;
        to: string;
      }).to);
      if (!hasActiveFilters) {
        setFilteredStories(null);
        setFilteredTotal(undefined);
        return;
      }
      const params = buildFilterParams(query, filters);
      const apiUrl = `/api/search-filter?${params.toString()}`;
      const result = await safeFetchJson(apiUrl);
      if (!result.success) {
        setFilterError(result.error || "Failed to load filtered results");
        setFilteredStories(null);
        setFilteredTotal(undefined);
        return;
      }
      const data = result.data;
      if (data?.error) {
        setFilterError(data.error);
        setFilteredStories(null);
        setFilteredTotal(undefined);
        return;
      }
      const stories = data?.items || [];
      const total = data?.total || 0;
      setFilteredStories(stories);
      setFilteredTotal(total);
    };
    $[2] = query;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleFilterChange = t2;
  const displayTotal = filteredTotal !== undefined ? filteredTotal : totalResults;
  const transformStoryToNewsItem = _temp;
  let t3;
  if ($[4] !== children || $[5] !== filterError || $[6] !== filteredStories) {
    t3 = () => {
      if (filterError) {
        return <div className="flex justify-center items-center py-8"><div className="text-red-500 text-sm">{filterError}</div></div>;
      }
      if (filteredStories && filteredStories.length === 0) {
        return <div className="flex justify-center items-center py-8"><div className="text-gray-500">No results found for the selected filters</div></div>;
      }
      if (filteredStories && filteredStories.length > 0) {
        return <div className="space-y-0">{filteredStories.map(story_0 => <div key={story_0.id} className="border border-DCDCDC"><HorizontalBigImageCategoryNewsSearch {...transformStoryToNewsItem(story_0)} /></div>)}</div>;
      }
      return children;
    };
    $[4] = children;
    $[5] = filterError;
    $[6] = filteredStories;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const renderFilteredResults = t3;
  let t4;
  if ($[8] !== handleFilterClick || $[9] !== query) {
    t4 = <SearchBar initialQuery={query} onFilterClick={handleFilterClick} showFilterIcon={true} />;
    $[8] = handleFilterClick;
    $[9] = query;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== displayTotal || $[12] !== query) {
    t5 = displayTotal !== undefined && <div className="text-sm text-gray-600 whitespace-nowrap px-4 pb-4 pt-0">{`${displayTotal.toLocaleString()} results found for "${query}"`}</div>;
    $[11] = displayTotal;
    $[12] = query;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t4 || $[15] !== t5) {
    t6 = <div className="border-b border-DCDCDC">{t4}{t5}</div>;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== renderFilteredResults) {
    t7 = renderFilteredResults();
    $[17] = renderFilteredResults;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t6 || $[20] !== t7) {
    t8 = <div className="w-full lg:w-[70%] border-b border-DCDCDC">{t6}{t7}</div>;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== filterData || $[23] !== handleFilterChange || $[24] !== rightSideContent || $[25] !== showFilter) {
    t9 = showFilter ? <SearchFilter filterData={filterData} onFilterChange={handleFilterChange} /> : rightSideContent;
    $[22] = filterData;
    $[23] = handleFilterChange;
    $[24] = rightSideContent;
    $[25] = showFilter;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== rightSideTopAd || $[28] !== t9) {
    t10 = <div className="w-full lg:w-[30%] border border-DCDCDC relative lg:sticky lg:top-8 h-fit">{rightSideTopAd}{t9}</div>;
    $[27] = rightSideTopAd;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== t10 || $[31] !== t8) {
    t11 = <div className="flex flex-col lg:flex-row lg:items-start border-b border-DCDCDC">{t8}{t10}</div>;
    $[30] = t10;
    $[31] = t8;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  return t11;
}
function _temp(story) {
  const section = story.sections?.[0];
  const heroImageKey = story["hero-image-s3-key"] || story.alternative?.home?.default?.["hero-image"]?.["hero-image-s3-key"];
  return {
    id: story.id,
    title: {
      value: story.headline || "No title available"
    },
    excerpt: {
      value: story.subheadline || ""
    },
    category: {
      value: section?.["display-name"] || "NEWS"
    },
    timeAgo: {
      value: formatTimeAgo(story["published-at"])
    },
    imageSrc: {
      value: heroImageKey ? `${MEDIA_BASE_URL}/${heroImageKey}` : FALLBACK_IMAGE
    },
    slug: story.slug
  };
}